import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Form, Spinner } from 'react-bootstrap';
import { Pie, Line } from 'react-chartjs-2';
import { OPCIONES_BASE, EJES_BASE, agruparEnOtros, datasetLinea } from '../utils/graficos';
import TarjetaKpi from '../components/ui/TarjetaKpi';
import { getTramitesParaEstadisticas } from '../services/tramiteService';
import { getTipoTramites } from '../services/tipoTramiteService';
import { colorTramite, bgTramite } from '../constants/tiposTramiteTurno';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import PageHeader from '../components/ui/PageHeader';
import '../style/pages/HomePage.css';
import useBreakpoint from '../hooks/useBreakpoint';
import BottomSheet from '../components/mobile/BottomSheet';
import BotonFiltros from '../components/mobile/BotonFiltros';
import ChipsFiltros from '../components/mobile/ChipsFiltros';


const MESES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

const BASE_OPTIONS = OPCIONES_BASE;

const normalizarTexto = (val) =>
    String(val || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

const formatearFechaMientrasEscribe = (valor) => {
    const n = valor.replace(/\D/g, '');
    if (!n.length) return '';
    if (n.length <= 2) return n;
    if (n.length <= 4) return `${n.slice(0,2)}/${n.slice(2)}`;
    return `${n.slice(0,2)}/${n.slice(2,4)}/${n.slice(4,8)}`;
};

const convertirTextoAFecha = (texto) => {
    if (!texto?.trim()) return null;
    const partes = texto.replace(/[^\d/]/g, '').split('/');
    if (partes.length === 3) {
        const [dia, mes, anio] = partes.map(Number);
        if (dia > 0 && dia <= 31 && mes > 0 && mes <= 12 && anio >= 1900 && anio <= 2100) {
            const f = new Date(anio, mes - 1, dia);
            if (!isNaN(f.getTime()) && f.getDate() === dia && f.getMonth() === mes - 1)
                return f.toISOString().split('T')[0];
        }
    }
    return null;
};

function ReportesTramites() {
    const navigate = useNavigate();
    const [tramites,     setTramites]     = useState([]);
    const [tiposTramite, setTiposTramite] = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [filtroFechaInicio,      setFiltroFechaInicio]      = useState('');
    const [filtroFechaFin,         setFiltroFechaFin]         = useState('');
    const [filtroFechaInicioTexto, setFiltroFechaInicioTexto] = useState('');
    const [filtroFechaFinTexto,    setFiltroFechaFinTexto]    = useState('');
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();
    const pieTipoRef  = useRef(null);
    const lineaMesRef = useRef(null);

    useEffect(() => {
        const cargar = async () => {
            try {
                setLoading(true);
                const [tramitesData, tiposData] = await Promise.all([getTramitesParaEstadisticas(), getTipoTramites()]);
                setTramites(tramitesData);
                setTiposTramite(tiposData);
            } catch (err) {
                console.error('Error cargando reportes:', err);
            } finally {
                setLoading(false);
            }
        };
        cargar();
    }, []);

    const resolverIdTipo = (tramite) => {
        if (!tramite) return null;
        if (tramite.idTipoTramite != null && tramite.idTipoTramite !== '') return Number(tramite.idTipoTramite);
        const nombre = tramite.nombreTipoTramite ||
            (typeof tramite.tipoTramite === 'string' ? tramite.tipoTramite : tramite.tipoTramite?.nombre);
        if (!nombre) return null;
        const n = normalizarTexto(nombre);
        return tiposTramite.find((t) => normalizarTexto(t.nombre) === n)?.idTipoTramite ?? null;
    };

    const obtenerNombreTipo = (idTipo) =>
        tiposTramite.find((t) => String(t.idTipoTramite) === String(idTipo))?.nombre || 'Sin tipo';

    const tramitesFiltrados = useMemo(() => {
        return tramites.filter((t) => {
            const fechaRaw = t.fechaCreacion || t.fechaEstadoActual;
            if (!fechaRaw) return !(filtroFechaInicio || filtroFechaFin);
            const f = new Date(fechaRaw);
            const fecha = new Date(f.getFullYear(), f.getMonth(), f.getDate());
            if (filtroFechaInicio) {
                const ini = new Date(filtroFechaInicio);
                if (fecha < new Date(ini.getFullYear(), ini.getMonth(), ini.getDate())) return false;
            }
            if (filtroFechaFin) {
                const fin = new Date(filtroFechaFin);
                if (fecha > new Date(fin.getFullYear(), fin.getMonth(), fin.getDate())) return false;
            }
            return true;
        });
    }, [tramites, filtroFechaInicio, filtroFechaFin]);

    const estadisticas = useMemo(() => {
        const total = tramitesFiltrados.length;
        const porTipo = {};
        tramitesFiltrados.forEach((t) => {
            const id = resolverIdTipo(t);
            const nombre = id ? obtenerNombreTipo(id) : 'Sin tipo';
            porTipo[nombre] = (porTipo[nombre] || 0) + 1;
        });
        const artCount = tramitesFiltrados.filter((t) => {
            const id = resolverIdTipo(t);
            return id ? normalizarTexto(obtenerNombreTipo(id)).includes('art') : false;
        }).length;
        const rtiCount = tramitesFiltrados.filter((t) => {
            const id = resolverIdTipo(t);
            return id ? normalizarTexto(obtenerNombreTipo(id)).includes('rti') : false;
        }).length;
        return { total, porTipo, artCount, rtiCount };
    }, [tramitesFiltrados, tiposTramite]);

    const tramitesPorMes = useMemo(() => {
        const conteos = {};
        tramitesFiltrados.forEach((t) => {
            const fechaRaw = t.fechaCreacion || t.fechaEstadoActual;
            if (!fechaRaw) return;
            const f = new Date(fechaRaw);
            const clave = `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}`;
            conteos[clave] = (conteos[clave] || 0) + 1;
        });
        let claves = Object.keys(conteos).sort();
        if (claves.length === 0) {
            const hoy = new Date();
            for (let i = 5; i >= 0; i--) {
                const d = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
                const c = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                conteos[c] = 0; claves.push(c);
            }
        }
        return {
            labels: claves.map((c) => { const [anio, mes] = c.split('-'); return `${MESES[parseInt(mes,10)-1]} ${anio}`; }),
            data:   claves.map((c) => conteos[c] || 0),
        };
    }, [tramitesFiltrados]);

    const datosPieTipos = useMemo(() => {
        // Paleta categórica del sistema (utils/graficos): los 5 tipos con más trámites con color
        // propio y el resto agrupado en "Otros" en gris, para que la torta no sea un arcoíris.
        const { labels, data, colores } = agruparEnOtros(
            Object.keys(estadisticas.porTipo), Object.values(estadisticas.porTipo)
        );
        return { labels, datasets: [{ data, backgroundColor: colores }] };
    }, [estadisticas.porTipo]);

    const datosLinea = useMemo(() => ({
        labels: tramitesPorMes.labels,
        datasets: [datasetLinea('Trámites ingresados', tramitesPorMes.data, 0, { relleno: true })],
    }), [tramitesPorMes]);

    const limpiarFiltros = () => {
        setFiltroFechaInicio(''); setFiltroFechaFin('');
        setFiltroFechaInicioTexto(''); setFiltroFechaFinTexto('');
    };

    const generarPDF = () => {
        const doc = new jsPDF();
        const pageW = doc.internal.pageSize.width;
        const pageH = doc.internal.pageSize.height;
        const m = 14; let y = 20;
        const salto = (n) => { if (y + n > pageH - 20) { doc.addPage(); y = 20; } };
        const tit = (txt, sz = 16) => {
            salto(14); doc.setFontSize(sz); doc.setFont(undefined, 'bold');
            doc.setTextColor(46, 80, 144); doc.text(txt, m, y); y += sz < 14 ? 7 : 10;
            doc.setFont(undefined, 'normal'); doc.setTextColor(0, 0, 0);
        };
        const lin = (txt) => { salto(6); doc.setFontSize(10); doc.text(txt, m, y); y += 6; };
        const sep = () => { salto(5); doc.setDrawColor(200,200,200); doc.line(m, y, pageW-m, y); y += 8; };

        doc.setFontSize(22); doc.setFont(undefined,'bold'); doc.setTextColor(46,80,144);
        doc.text('Reporte General de Trámites', pageW/2, 55, { align: 'center' });
        doc.setFontSize(11); doc.setFont(undefined,'normal'); doc.setTextColor(100,100,100);
        doc.text(`Generado el ${new Date().toLocaleDateString('es-ES',{day:'2-digit',month:'long',year:'numeric'})}`, pageW/2, 68, { align: 'center' });

        y = 90; tit('Filtros aplicados', 12);
        lin(`• Período: ${filtroFechaInicio || 'Sin límite'} → ${filtroFechaFin || 'Sin límite'}`);
        lin(`• Total de trámites: ${estadisticas.total}`);

        doc.addPage(); y = 20; tit('RESUMEN GENERAL'); sep();
        Object.entries(estadisticas.porTipo).forEach(([tipo, cant]) => {
            lin(`• ${tipo}: ${cant} trámites (${estadisticas.total > 0 ? ((cant/estadisticas.total)*100).toFixed(1) : 0}%)`);
        });

        const pieImg  = pieTipoRef.current?.toBase64Image();
        const lineImg = lineaMesRef.current?.toBase64Image();
        if (pieImg)  { doc.addPage(); y = 20; tit('DISTRIBUCIÓN POR TIPO'); sep(); doc.addImage(pieImg,'PNG',m+15,y,pageW-(m*2)-30,100); y+=110; }
        if (lineImg) { doc.addPage(); y = 20; tit('INGRESOS POR MES');       sep(); doc.addImage(lineImg,'PNG',m,y,pageW-(m*2),90);   y+=100; }

        doc.addPage(); y = 20; tit('DETALLE POR TIPO'); sep();
        autoTable(doc, {
            head: [['Tipo de Trámite','Cantidad','Porcentaje']],
            body: Object.entries(estadisticas.porTipo).map(([tipo,cant]) => [tipo, cant, `${estadisticas.total>0?((cant/estadisticas.total)*100).toFixed(1):0}%`]),
            startY: y,
            styles: { fontSize: 10, cellPadding: 3 },
            headStyles: { fillColor:[46,80,144], textColor:255, fontStyle:'bold' },
            alternateRowStyles: { fillColor:[245,245,245] },
        });

        const totalPgs = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPgs; i++) {
            doc.setPage(i); doc.setFontSize(8); doc.setTextColor(150,150,150);
            doc.text(`Página ${i} de ${totalPgs} — Estudio Jurídico — Reporte General`, pageW/2, pageH-10, { align:'center' });
        }
        doc.save(`reporte_tramites_general_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    if (loading) return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
            <Spinner animation="border" variant="primary" />
            <span className="ms-3">Cargando reportes...</span>
        </div>
    );

    const artPct = estadisticas.total > 0 ? ((estadisticas.artCount / estadisticas.total) * 100).toFixed(1) : '0.0';
    const rtiPct = estadisticas.total > 0 ? ((estadisticas.rtiCount / estadisticas.total) * 100).toFixed(1) : '0.0';

    return (
        <div className="homepage-container area-reportes">
            <PageHeader
                variante="plano"
                icono="bar-chart-fill"
                titulo="Reportes de Trámites"
                descripcion="Vista general — comparación entre tipos de trámite"
                accion={
                    <button
                        type="button"
                        className="btn btn-outline-primary d-flex align-items-center gap-2"
                        onClick={generarPDF}
                    >
                        <i className="bi bi-file-earmark-pdf" />
                        Exportar PDF
                    </button>
                }
            />

            {/* Accesos ART / RTI */}
            <div className="accesos-rapidos-home">
                <div className="acceso-card acceso-card-art" onClick={() => navigate('/reportes/tramites/art')}>
                    <div className="acceso-card-icon"><i className="bi bi-heart-pulse-fill" /></div>
                    <div className="acceso-card-body">
                        <h5>Reportes ART</h5>
                        <p>{estadisticas.artCount} trámites · {artPct}% del total — Distribución por estado, cantidad y tiempos promedio</p>
                    </div>
                    <i className="bi bi-arrow-right-short acceso-card-arrow" />
                </div>
                <div className="acceso-card acceso-card-agenda" onClick={() => navigate('/reportes/tramites/rti')}>
                    <div className="acceso-card-icon"><i className="bi bi-folder-fill" /></div>
                    <div className="acceso-card-body">
                        <h5>Reportes RTI</h5>
                        <p>{estadisticas.rtiCount} trámites · {rtiPct}% del total — Distribución por estado, cantidad y tiempos promedio</p>
                    </div>
                    <i className="bi bi-arrow-right-short acceso-card-arrow" />
                </div>
            </div>

            {/* Filtros */}
            {(() => {
                const campoDesde = (
                    <Form.Group>
                        <Form.Label >
                            <i className="bi bi-calendar-event me-1" />Fecha Desde
                        </Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                type="text" placeholder="dd/mm/aaaa"
                                value={filtroFechaInicioTexto || (filtroFechaInicio ? new Date(filtroFechaInicio+'T00:00:00').toLocaleDateString('es-AR',{day:'2-digit',month:'2-digit',year:'numeric'}) : '')}
                                onChange={e => {
                                    const fmt = formatearFechaMientrasEscribe(e.target.value);
                                    setFiltroFechaInicioTexto(fmt);
                                    const iso = convertirTextoAFecha(fmt);
                                    if (iso) setFiltroFechaInicio(iso);
                                    else if (!e.target.value) setFiltroFechaInicio('');
                                }}
                                onBlur={e => { if (e.target.value && !convertirTextoAFecha(e.target.value)) { setFiltroFechaInicioTexto(''); setFiltroFechaInicio(''); } }}
                                style={{ flex:1 }}
                            />
                            <Form.Control
                                type="date" value={filtroFechaInicio}
                                onChange={e => { setFiltroFechaInicio(e.target.value); setFiltroFechaInicioTexto(e.target.value ? new Date(e.target.value+'T00:00:00').toLocaleDateString('es-AR',{day:'2-digit',month:'2-digit',year:'numeric'}) : ''); }}
                                style={{ width:'45px', minWidth:'45px' }}
                            />
                        </div>
                    </Form.Group>
                );
                const campoHasta = (
                    <Form.Group>
                        <Form.Label >
                            <i className="bi bi-calendar-x me-1" />Fecha Hasta
                        </Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                type="text" placeholder="dd/mm/aaaa"
                                value={filtroFechaFinTexto || (filtroFechaFin ? new Date(filtroFechaFin+'T00:00:00').toLocaleDateString('es-AR',{day:'2-digit',month:'2-digit',year:'numeric'}) : '')}
                                onChange={e => {
                                    const fmt = formatearFechaMientrasEscribe(e.target.value);
                                    setFiltroFechaFinTexto(fmt);
                                    const iso = convertirTextoAFecha(fmt);
                                    if (iso) setFiltroFechaFin(iso);
                                    else if (!e.target.value) setFiltroFechaFin('');
                                }}
                                onBlur={e => { if (e.target.value && !convertirTextoAFecha(e.target.value)) { setFiltroFechaFinTexto(''); setFiltroFechaFin(''); } }}
                                style={{ flex:1 }}
                            />
                            <Form.Control
                                type="date" value={filtroFechaFin} min={filtroFechaInicio || undefined}
                                onChange={e => { setFiltroFechaFin(e.target.value); setFiltroFechaFinTexto(e.target.value ? new Date(e.target.value+'T00:00:00').toLocaleDateString('es-AR',{day:'2-digit',month:'2-digit',year:'numeric'}) : ''); }}
                                style={{ width:'45px', minWidth:'45px' }}
                            />
                        </div>
                    </Form.Group>
                );

                const chipsFiltros = [
                    filtroFechaInicio && { key: 'desde', label: `Desde: ${filtroFechaInicioTexto || filtroFechaInicio}`, onQuitar: () => { setFiltroFechaInicio(''); setFiltroFechaInicioTexto(''); } },
                    filtroFechaFin && { key: 'hasta', label: `Hasta: ${filtroFechaFinTexto || filtroFechaFin}`, onQuitar: () => { setFiltroFechaFin(''); setFiltroFechaFinTexto(''); } },
                ].filter(Boolean);

                return esMobile ? (
                    <div className="eventos-section" style={{ marginBottom: '2rem' }}>
                        <div className="eventos-header-row">
                            <h2 className="eventos-section-title">
                                <i className="bi bi-funnel me-2" />Filtros Generales
                            </h2>
                        </div>
                        <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                        <ChipsFiltros chips={chipsFiltros} />
                        {sheetFiltrosAbierto && (
                            <BottomSheet
                                titulo="Filtros"
                                onCerrar={() => setSheetFiltrosAbierto(false)}
                                accionPrimaria={chipsFiltros.length > 0 ? { label: 'Limpiar filtros', onClick: limpiarFiltros } : undefined}
                            >
                                <div className="mb-3">{campoDesde}</div>
                                {campoHasta}
                            </BottomSheet>
                        )}
                    </div>
                ) : (
                    <div className="eventos-section" style={{ marginBottom: '2rem' }}>
                        <div className="eventos-header-row">
                            <h2 className="eventos-section-title">
                                <i className="bi bi-funnel me-2" />Filtros Generales
                            </h2>
                            <button
                                type="button"
                                className="btn-ver-calendario-home"
                                style={{ background: 'var(--text-muted)', boxShadow: 'none' }}
                                onClick={limpiarFiltros}
                            >
                                <i className="bi bi-x-circle me-2" />Limpiar filtros
                            </button>
                        </div>
                        <Row>
                            <Col md={6}>{campoDesde}</Col>
                            <Col md={6}>{campoHasta}</Col>
                        </Row>
                    </div>
                );
            })()}

            {/* KPIs */}
            <Row className="mb-4 g-3">
                {[
                    { label:'Total trámites', valor:estadisticas.total, sub:'en el período', icono:'folder2-open', tono:'primario', onClick: null },
                    { label:'Trámites ART',   valor:estadisticas.artCount, sub:`${artPct}% del total`, icono:'cone-striped', tono:'advertencia', onClick:()=>navigate('/reportes/tramites/art') },
                    { label:'Trámites RTI',   valor:estadisticas.rtiCount, sub:`${rtiPct}% del total`, icono:'person-wheelchair', tono:'exito', onClick:()=>navigate('/reportes/tramites/rti') },
                ].map((kpi) => (
                    <Col md={4} key={kpi.label}>
                        <TarjetaKpi {...kpi} />
                    </Col>
                ))}
            </Row>

            {/* Gráficos */}
            <Row className="g-4 mb-4">
                <Col lg={5}>
                    <div className="eventos-section" style={{ height:'100%' }}>
                        <h2 className="eventos-section-title" style={{ fontSize:'1.2rem', marginBottom:'0.4rem' }}>
                            <i className="bi bi-pie-chart-fill me-2" />Distribución por Tipo
                        </h2>
                        <p className="text-muted mb-3" style={{ fontSize:'0.875rem' }}>Proporción de trámites por tipo</p>
                        <Pie
                            key={`pie-${tramitesFiltrados.length}-${filtroFechaInicio}-${filtroFechaFin}`}
                            ref={pieTipoRef}
                            data={datosPieTipos}
                            options={{
                                ...BASE_OPTIONS,
                                plugins: {
                                    ...BASE_OPTIONS.plugins,
                                    legend: { ...BASE_OPTIONS.plugins.legend, position:'right' },
                                    tooltip: { ...BASE_OPTIONS.plugins.tooltip, callbacks: { label: (ctx) => { const v=ctx.parsed||0; const p=estadisticas.total>0?((v/estadisticas.total)*100).toFixed(1):0; return `${ctx.label}: ${v} (${p}%)`; } } },
                                },
                            }}
                        />
                    </div>
                </Col>
                <Col lg={7}>
                    <div className="eventos-section" style={{ height:'100%' }}>
                        <h2 className="eventos-section-title" style={{ fontSize:'1.2rem', marginBottom:'0.4rem' }}>
                            <i className="bi bi-graph-up me-2" />Ingresos por Mes
                        </h2>
                        <p className="text-muted mb-3" style={{ fontSize:'0.875rem' }}>Cantidad de trámites ingresados mes a mes</p>
                        <Line
                            key={`line-${tramitesFiltrados.length}-${filtroFechaInicio}-${filtroFechaFin}`}
                            ref={lineaMesRef}
                            data={datosLinea}
                            options={{
                                ...BASE_OPTIONS,
                                scales: {
                                    x: { ...EJES_BASE.x, ticks:{ ...EJES_BASE.x.ticks, maxRotation:45 } },
                                    y: { ...EJES_BASE.y, ticks:{ ...EJES_BASE.y.ticks, precision:0 } },
                                },
                                plugins: {
                                    ...BASE_OPTIONS.plugins,
                                    legend: { display:false },
                                    tooltip: { ...BASE_OPTIONS.plugins.tooltip, callbacks: { label:(ctx)=>`${ctx.parsed.y} trámite${ctx.parsed.y!==1?'s':''}` } },
                                },
                            }}
                        />
                    </div>
                </Col>
            </Row>

            {/* Tabla resumen */}
            <div className="eventos-section">
                <h2 className="eventos-section-title" style={{ fontSize:'1.2rem', marginBottom:'0.4rem' }}>
                    <i className="bi bi-table me-2" />Resumen por Tipo
                </h2>
                <p className="text-muted mb-3" style={{ fontSize:'0.875rem' }}>Cantidad y porcentaje de trámites por tipo, con acceso directo al reporte detallado.</p>
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                        <tr>
                            <th>Tipo de Trámite</th>
                            <th className="text-center">Cantidad</th>
                            <th className="text-center">Porcentaje</th>
                            <th className="text-center">Acceso</th>
                        </tr>
                        </thead>
                        <tbody>
                        {Object.entries(estadisticas.porTipo).map(([tipo, cant]) => {
                            const pct   = estadisticas.total > 0 ? ((cant/estadisticas.total)*100).toFixed(1) : '0.0';
                            const isART = normalizarTexto(tipo).includes('art');
                            const isRTI = normalizarTexto(tipo).includes('rti');
                            const color = colorTramite(tipo);
                            return (
                                <tr key={tipo}>
                                    <td>
                                            <span style={{ background: bgTramite(tipo), color: 'var(--text)', fontWeight:'500', padding:'0.25rem 0.75rem', borderRadius:'var(--radius-pill)', fontSize:'var(--fs-chico)', display:'inline-flex', alignItems:'center', gap:'6px' }}>
                                                <span aria-hidden="true" style={{ width:8, height:8, borderRadius:'50%', background: color }} />
                                                {tipo}
                                            </span>
                                    </td>
                                    <td className="text-center fw-bold">{cant}</td>
                                    <td className="text-center">
                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                            <div className="progress flex-grow-1" style={{ height:'8px', maxWidth:'90px' }}>
                                                <div className="progress-bar" style={{ width:`${pct}%`, background: 'var(--primary)' }} />
                                            </div>
                                            <span style={{ minWidth:'42px', fontSize:'0.9rem' }}>{pct}%</span>
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        {isART && <button type="button" className="btn-tramites-art" style={{ fontSize:'0.82rem' }} onClick={() => navigate('/reportes/tramites/art')}>Ver reporte →</button>}
                                        {isRTI && <button type="button" className="btn-tramites-rti" style={{ fontSize:'0.82rem' }} onClick={() => navigate('/reportes/tramites/rti')}>Ver reporte →</button>}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                    {Object.keys(estadisticas.porTipo).length === 0 && (
                        <div className="empty-state-eventos compact mt-3">
                            <i className="bi bi-inbox" />
                            <p className="mb-0">No se encontraron trámites con los filtros seleccionados.</p>
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
}

export default ReportesTramites;